import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import Table, {
  dateFormatter,
  buttonsStyle,
} from "../../../components/tables/table";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import {
  getStrategies,
  deleteStrategy,
  changeStatusStrategy,
} from "../../../../api/strategy";
import { Button, Tooltip, Switch, FormControlLabel } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import { alertError, alertSuccess } from "../../../../utils/logger";
import { alertStrategyConflictActivate } from "../../../../utils/strategyConflictAlert";
import { useHistory } from "react-router-dom";
import ToggleOffIcon from "@material-ui/icons/ToggleOff";
import ToggleOnIcon from "@material-ui/icons/ToggleOn";
import { shallowEqual, useSelector } from "react-redux";
import FiltersCard from "../../../components/filters/Filter";

function sortByAlertStrategy(a, b) {
  const aA = a?.alertStrategy, bA = b?.alertStrategy;
  if (aA && !bA) return -1;
  if (!aA && bA) return 1;
  return 0;
}

function getData(strategies) {
  let data = [];
  
  for (let i = 0; i < strategies.length; ++i) {
    const elem = {};
    elem.name = strategies[i].fullName;
    elem.description = strategies[i].description;
    elem.createdAt = strategies[i].createdAt;
    elem.id = strategies[i]._id;
    elem.active = strategies[i].active;
    elem.alertStrategy = strategies[i]?.alertStrategy;
    data = data.concat(elem);
  }

  data.sort(sortByAlertStrategy);
  return data;
}

const initialFilters = {
  active: false,
};

export default function StrategiesPage() {
  const [data, setData] = useState([]);
  const [strategyId, setStrategyId] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [strategies, setStrategies] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [collapsed, setCollapsed] = useState(true);
  const [filterOptions, setFilterOptions] = useState(initialFilters);
  const history = useHistory();
  const user = useSelector((store) => store.authentication?.user, shallowEqual);

  function buttonFormatter(cell) {
    const elem = data.find((item) => item._id === cell);
    return (
      <>
        <Tooltip title="Edit">
          <Button
            style={buttonsStyle}
            size="small"
            onClick={() => history.push("/edit-strategy/" + cell)}
          >
            <EditIcon />
          </Button>
        </Tooltip>
        {user?.role === "admin" && (
          <>
            <Tooltip title={elem?.active ? "Disable" : "Enable"}>
              <Button
                style={buttonsStyle}
                size="small"
                onClick={() => {
                  setStrategyId(elem);
                  setOpenConfirmDialog(1);
                }}
              >
                {elem?.active ? (
                  <ToggleOffIcon />
                ) : (
                  <ToggleOnIcon style={{ color: "red" }} />
                )}
              </Button>
            </Tooltip>
            { !elem?.alertStrategy && (
              <Tooltip title="Delete">
                <Button
                  style={buttonsStyle}
                  size="small"
                  onClick={() => {
                    setStrategyId(elem);
                    setOpenConfirmDialog(2);
                  }}
                >
                  <DeleteIcon />
                </Button>
              </Tooltip>
            )}
          </>
        )}
      </>
    );
  }

  const columns = [
    { dataField: "name", text: "Full Name", sort: true },
    { dataField: "description", text: "Description", sort: true },
    {
      dataField: "createdAt",
      text: "Created at",
      formatter: dateFormatter,
      sort: true,
    },
    { dataField: "id", text: "", formatter: buttonFormatter },
  ];

  useEffect(() => {
    getStrategies()
      .then((res) => {
        if (res.status === 200) {
          setData(res.data);
          setFilteredData(res.data);
          setStrategies(res.data);
          setRefresh(false);
        }
      })
      .catch((error) => {
        alertError({
          error: error,
          customMessage: "Could not get strategies.",
        });
      });
  }, [refresh]);

  const handleSearch = async () => {
    if (!data.length) return;
    setFilteredData(
      data.filter((item) => {
        let filter = true;
        if (filterOptions.active) filter = filter && item.active;
        if (filter) return item;
        return false;
      })
    );
  };

  const handleClearFilters = () => {
    setFilterOptions(initialFilters);
    setRefresh(true);
  };

  const renderFiltersContent = () => {
    return (
      <>
        <br />
        <FormControlLabel
          control={
            <Switch
              checked={filterOptions.active}
              onChange={() =>
                setFilterOptions({
                  ...filterOptions,
                  active: !filterOptions.active,
                })
              }
              name="checkActive"
            />
          }
          label={filterOptions.active ? "Only active" : "All active/inactive"}
        />
      </>
    );
  };

  return (
    <>
      <Card>
        <CardHeader title="Strategies list">
          <CardHeaderToolbar>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => history.push("/edit-strategy")}
            >
              Add new
            </button>
          </CardHeaderToolbar>
        </CardHeader>
        <CardBody>
          <FiltersCard
            filtersContent={renderFiltersContent}
            collapsed={collapsed}
            setCollapsed={setCollapsed}
            handleClearFilters={handleClearFilters}
            handleSearch={handleSearch}
          />

          <Table data={getData(filteredData, strategies)} columns={columns} />

          <ConfirmDialog
            title={`Are you sure you want to ${
              strategyId?.active ? "disable" : "enable"
            } this strategy?`}
            open={openConfirmDialog === 1}
            setOpen={setOpenConfirmDialog}
            onConfirm={() => {
              changeStatusStrategy(strategyId._id, !strategyId?.active)
                .then((res) => {
                  if (res.status === 200) {
                    alertSuccess({
                      title: `${strategyId?.active ? "Disabled!" : "Enabled!"}`,
                      customMessage: `Strategy ${
                        strategyId?.active ? "disabled" : "enabled"
                      } successfully`,
                    });
                    setRefresh(true);
                  }
                })
                .catch(async (error) => {
                  if (
                    error?.response?.status === 409 &&
                    error.response.data?.blockingStrategies?.length
                  ) {
                    await alertStrategyConflictActivate(
                      error.response.data.blockingStrategies,
                    );
                  } else {
                    alertError({
                      error: error,
                      customMessage: `Could not ${
                        strategyId?.active ? "disable" : "enable"
                      } strategy.`,
                    });
                  }
                });
            }}
          />
          <ConfirmDialog
            title={"Are you sure you want to remove this strategy?"}
            open={openConfirmDialog === 2}
            setOpen={setOpenConfirmDialog}
            onConfirm={() => {
              deleteStrategy(strategyId?._id)
                .then((res) => {
                  if (res.status === 204 || res.status === 200) {
                    alertSuccess({
                      title: "Deleted!",
                      customMessage: "strategy removed successfully.",
                    });
                    setRefresh(true);
                  }
                })
                .catch((error) => {
                  alertError({
                    error: error,
                    customMessage: "Could not remove strategy.",
                  });
                });
            }}
          />
        </CardBody>
      </Card>
    </>
  );
}
